/**
 * The announcement's content as JSON, and back.
 *
 * The fields tab and the JSON tab are two views of the same content, so this module is the single
 * place that converts between them. Everything outside the content — tenant, version, page, use
 * case — belongs to step 1 and is deliberately absent from this JSON.
 *
 * Parsing is **lenient on purpose**. It runs on every keystroke, so it only rejects what cannot be
 * read at all (a syntax error, or a root that is not an object); anything missing becomes an empty
 * default. Strict validation would wipe the fields the moment someone typed an opening brace.
 *
 * @module Pages/BUs/WhatsNew
 */

import { isKnownIcon } from '@/Shared/icons/iconCatalog';
import type {
  ItemTranslation,
  Language,
  Translation,
  WhatsNewFormData,
  WhatsNewFormItem,
} from '../Types/WhatsNewForm.types';

export const LANGUAGES: Language[] = ['pt', 'en', 'es'];

/** The content half of the form — what the JSON tab shows. */
export type WhatsNewContent = Pick<
  WhatsNewFormData,
  'welcomeMessage' | 'subtitle' | 'footer' | 'confirmButtonText' | 'items' | 'actionParams'
>;

function emptyTranslation(): Translation {
  return { pt: '', en: '', es: '' };
}

function emptyItemTranslation(): ItemTranslation {
  return {
    pt: { title: '', body: '' },
    en: { title: '', body: '' },
    es: { title: '', body: '' },
  };
}

/** A fresh item, so "Adicionar item" never produces an undefined field. */
export function createEmptyItem(order: number): WhatsNewFormItem {
  return {
    icon: 'Communication.Megaphone',
    color: '#0033FF',
    title: '',
    body: '',
    order,
    translations: emptyItemTranslation(),
    privileges: [],
  };
}

/** Content with nothing filled in — the starting point of a new announcement. */
export function createEmptyContent(): WhatsNewContent {
  return {
    welcomeMessage: emptyTranslation(),
    subtitle: emptyTranslation(),
    footer: emptyTranslation(),
    confirmButtonText: emptyTranslation(),
    items: [createEmptyItem(0)],
    actionParams: undefined,
  };
}

/**
 * Content as the JSON the editor shows.
 *
 * Key order is fixed so that editing a field does not reshuffle the document under the cursor.
 * `actionParams` is omitted entirely when empty rather than written as `{}`, so a pasted document
 * round-trips to the same text.
 */
export function serializeContent(content: WhatsNewContent): string {
  const document: Record<string, unknown> = {
    welcomeMessage: content.welcomeMessage,
    subtitle: content.subtitle,
    footer: content.footer,
    confirmButtonText: content.confirmButtonText,
    items: content.items.map(item => ({
      icon: item.icon,
      color: item.color,
      title: item.title,
      body: item.body,
      order: item.order,
      translations: item.translations,
      ...(item.privileges && item.privileges.length > 0 ? { privileges: item.privileges } : {}),
    })),
  };

  if (content.actionParams && Object.keys(content.actionParams).length > 0) {
    document['actionParams'] = content.actionParams;
  }

  return JSON.stringify(document, null, 2);
}

export type ParseContentResult =
  | { ok: true; content: WhatsNewContent }
  | { ok: false; message: string };

/**
 * Reads a translated text.
 *
 * Accepts two shapes, because two different documents get pasted in here: the editor's own
 * `{ pt, en, es }`, and the announcement as the BFF returns it — which is what the "Copiar JSON"
 * button on a card produces, where the same text is a plain string in Portuguese.
 */
function readTranslation(source: unknown): Translation {
  if (typeof source === 'string') {
    return { ...emptyTranslation(), pt: source };
  }

  if (typeof source !== 'object' || source === null || Array.isArray(source)) {
    return emptyTranslation();
  }

  const value = source as Record<string, unknown>;
  return {
    pt: typeof value['pt'] === 'string' ? value['pt'] : '',
    en: typeof value['en'] === 'string' ? value['en'] : '',
    es: typeof value['es'] === 'string' ? value['es'] : '',
  };
}

/**
 * Reads an item's translations.
 *
 * Same two shapes as {@link readTranslation}: the editor keys them by language, while the BFF
 * returns a list of rows carrying their own `language`. An array would otherwise pass the
 * `typeof === 'object'` check and yield nothing, which is exactly how a pasted announcement came
 * out with every field blank.
 */
function readItemTranslation(source: unknown): ItemTranslation {
  const base = emptyItemTranslation();
  if (typeof source !== 'object' || source === null) return base;

  if (Array.isArray(source)) {
    for (const entry of source) {
      if (typeof entry !== 'object' || entry === null) continue;

      const row = entry as Record<string, unknown>;
      const language = row['language'];
      if (typeof language !== 'string' || !LANGUAGES.includes(language as Language)) continue;

      base[language as Language] = {
        title: typeof row['title'] === 'string' ? row['title'] : '',
        body: typeof row['body'] === 'string' ? row['body'] : '',
      };
    }

    return base;
  }

  const value = source as Record<string, unknown>;
  for (const language of LANGUAGES) {
    const entry = value[language];
    if (typeof entry === 'object' && entry !== null) {
      const typed = entry as Record<string, unknown>;
      base[language] = {
        title: typeof typed['title'] === 'string' ? typed['title'] : '',
        body: typeof typed['body'] === 'string' ? typed['body'] : '',
      };
    }
  }

  return base;
}

/**
 * Reads the JSON the person typed.
 *
 * @returns The content, or the parser's own message — which is what the status line shows, so it
 *   has to be the real error rather than a rewritten one.
 */
export function parseContent(raw: string): ParseContentResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : String(error) };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, message: 'O JSON precisa ser um objeto na raiz.' };
  }

  const document = parsed as Record<string, unknown>;
  const rawItems = Array.isArray(document['items']) ? (document['items'] as unknown[]) : [];

  const items: WhatsNewFormItem[] = rawItems.map((entry, index) => {
    const item = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<
      string,
      unknown
    >;
    const privileges = Array.isArray(item['privileges'])
      ? (item['privileges'] as unknown[]).filter((p): p is string => typeof p === 'string')
      : [];

    return {
      icon: typeof item['icon'] === 'string' ? item['icon'] : '',
      color: typeof item['color'] === 'string' ? item['color'] : '#0033FF',
      title: typeof item['title'] === 'string' ? item['title'] : '',
      body: typeof item['body'] === 'string' ? item['body'] : '',
      order: typeof item['order'] === 'number' ? item['order'] : index,
      translations: readItemTranslation(item['translations']),
      privileges,
    };
  });

  const actionParams =
    typeof document['actionParams'] === 'object' && document['actionParams'] !== null
      ? (document['actionParams'] as WhatsNewContent['actionParams'])
      : undefined;

  return {
    ok: true,
    content: {
      welcomeMessage: readTranslation(document['welcomeMessage']),
      subtitle: readTranslation(document['subtitle']),
      footer: readTranslation(document['footer']),
      confirmButtonText: readTranslation(document['confirmButtonText']),
      items,
      actionParams,
    },
  };
}

/** How each language is named in the messages the person reads. */
const LANGUAGE_NAME: Record<Language, string> = {
  pt: 'Português',
  en: 'Inglês',
  es: 'Espanhol',
};

/**
 * What is still missing before the announcement can be created.
 *
 * Mirrors what the BFF requires, because it rejects the whole payload otherwise and the person
 * only finds out at the last step, as a wall of `items.1.translations.2.title should not be
 * empty`. The payload always carries a row for all three languages, so all three have to be
 * filled — the welcome message and, for every item, title and body.
 *
 * Subtitle, footer and the button label are optional on the BFF and stay optional here.
 *
 * @returns One line per problem, in reading order, empty when there is nothing to fix.
 */
export function getContentProblems(content: WhatsNewContent): string[] {
  const problems: string[] = [];

  for (const language of LANGUAGES) {
    if (content.welcomeMessage[language].trim().length === 0) {
      problems.push(`Preencha o welcomeMessage em ${LANGUAGE_NAME[language]}.`);
    }
  }

  if (content.items.length === 0) {
    problems.push('Adicione pelo menos um item.');
    return problems;
  }

  content.items.forEach((item, index) => {
    const label = `Item ${index + 1}`;

    if (item.icon.trim().length === 0) {
      problems.push(`${label}: escolha um ícone.`);
    } else if (!isKnownIcon(item.icon)) {
      /*
        The BFF takes any string here, so a wrong name only shows up in the dashboard — as the
        words "Icon not found" where the icon should be. Caught before it can be published.
      */
      problems.push(`${label}: o ícone "${item.icon.trim()}" não existe. Escolha um da lista.`);
    }
    if (item.color.trim().length === 0) problems.push(`${label}: escolha uma cor.`);

    for (const language of LANGUAGES) {
      const translation = item.translations[language];
      /*
        Portuguese also has to land in the item's flat title/body, which is what the BFF stores as
        the default language. Editing the Português tab writes both, so one message covers both.
      */
      const isFilled =
        translation.title.trim().length > 0 &&
        translation.body.trim().length > 0 &&
        (language !== 'pt' || (item.title.trim().length > 0 && item.body.trim().length > 0));

      if (!isFilled) {
        problems.push(`${label}: preencha título e texto em ${LANGUAGE_NAME[language]}.`);
      }
    }
  });

  return problems;
}

/** Whether there is enough content to move on: something to say, in every language. */
export function isContentUsable(content: WhatsNewContent): boolean {
  return getContentProblems(content).length === 0;
}
